import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

interface Props {
  page: number;
  pageSize: number;
  totalPages: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  pageSizeOptions?: number[];
  contentLength: number;
  defaultPageSize?: number;
  storageKey: string;
}

const Pagination = ({
  page,
  pageSize,
  totalPages,
  setPage,
  setPageSize,
  pageSizeOptions = [5, 10, 25],
  contentLength,
  defaultPageSize = pageSizeOptions[0],
  storageKey,
}: Props) => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const getNumberFromStorage = (key: string): number | null => {
    const value = localStorage.getItem(key);
    return value ? Number(value) : null;
  };
  
  useEffect(() => {
    const loadPageSettings = () => {
      const page = params.get("page");
      const pageSize = params.get("pageSize");
      const pageSizeFromStorage = getNumberFromStorage(storageKey);
  
      if (!pageSize) {
        const newPageSize = pageSizeFromStorage ?? defaultPageSize;
        setParams((params) => {
          params.set("pageSize", String(newPageSize));
          return params;
        });
        setPageSize(newPageSize);
      } else {
        setPageSize(Number(pageSize));
      }
  
      setPage(page ? Number(page) : 1);
    };
  
    loadPageSettings();
  }, [defaultPageSize, storageKey, params, setPage, setPageSize, setParams]);
  
  const handlePageSizeChange = (value: string) => {
    const newPageSize = Number(value);
    setParams((params) => {
      params.set("pageSize", value);
      params.delete("page");
      return params;
    });
    localStorage.setItem(storageKey, String(newPageSize));
    setPageSize(newPageSize);
  };
  

  const handleNextPage = () => {
    if (page < totalPages) {
      setParams((params) => {
        params.set("page", String(page + 1));
        return params;
      });
    }
  };
  const handlePrevPage = () => {
    if (page > 1) {
      if (page > 2) {
        setParams((params) => {
          params.set("page", String(page - 1));
          return params;
        });
      } else {
        setParams((params) => {
          params.delete("page");
          return params;
        });
      }
    }
  };

  return contentLength < Math.min(...pageSizeOptions) && totalPages < 2 ? (
    <></>
  ) : (
    <div className="grid grid-cols-pagination items-center">
      <div className="flex items-center gap-1">
        <span className="text-[12px] text-[grey]">{t("number_per_page")}</span>
        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="h-fit p-1 border-0 bg-[#F5F5F5] rounded-[8px] text-[12px] text-[grey] w-12">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="w-full border-2 border-gray-300 rounded-[8px]">
            {pageSizeOptions?.map((item) => (
              <SelectItem
                className="text-[#999] text-[16px] pl-2"
                value={String(item)}
                key={String(item)}
              >
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-4 text-[grey]">
        <div
          className={`flex items-center gap-2 ${
            page > 1 ? "text-blue-medium cursor-pointer" : ""
          }`}
          onClick={handlePrevPage}
        >
          <ChevronLeft />
          <span>{t("last_page")}</span>
        </div>
        <div className="border-2 border-grey-100 py-2 px-4 rounded-[10px]">
          {page} {t("of")} {totalPages}
        </div>
        <div
          className={`flex items-center gap-2 ${
            page < totalPages ? "text-blue-medium cursor-pointer" : ""
          }`}
          onClick={handleNextPage}
        >
          <span>{t("next_page")}</span>
          <ChevronRight />
        </div>
      </div>
    </div>
  );
};

export default Pagination;
